'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { motion } from 'framer-motion';
import {
  Coins,
  Cpu,
  LayoutGrid,
  Pill,
  Server,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import Navbar from '@/components/LandingPage/Navbar';
import Footer from '@/components/LandingPage/Footer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PartnersTable from '@/components/Partners/PartnersTable';
import { PARTNER_CATEGORIES } from '@/data/partners';
import { INVESTMENT_SECTORS_PATH } from '@/lib/site';

const CATEGORY_ICONS = { Cpu, Coins, Pill, Server };

const VALUE_PILLS = [
  { icon: ShieldCheck, label: 'Audited & compliant' },
  { icon: TrendingUp, label: 'Diversified sectors' },
  { icon: Sparkles, label: 'Best-in-class operators' },
];

const triggerBase =
  'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all data-[state=active]:border data-[state=active]:border-amber-500/40 data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-400 data-[state=inactive]:text-text-muted data-[state=inactive]:hover:bg-bg-secondary';

const VALID_TAB_IDS = new Set(PARTNER_CATEGORIES.map((c) => c.id));

export default function InvestmentSectorsPage() {
  const router = useRouter();
  const [tab, setTab] = useState(PARTNER_CATEGORIES[0]?.id || 'mining');
  const [quotes, setQuotes] = useState(null);

  useEffect(() => {
    if (!router.isReady) return;
    const raw = router.query.tab;
    const t = Array.isArray(raw) ? raw[0] : raw;
    if (typeof t === 'string' && VALID_TAB_IDS.has(t)) setTab(t);
  }, [router.isReady, router.query.tab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/partners/quotes');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setQuotes(data.quotes || {});
      } catch (_e) {
        if (cancelled) return;
        setQuotes(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTabChange = (next) => {
    setTab(next);
    router.replace(
      { pathname: INVESTMENT_SECTORS_PATH, query: { tab: next } },
      undefined,
      { shallow: true }
    );
  };

  return (
    <>
      <Navbar />
      <main>
        <section
          id="investment-sectors-hero"
          className="ember-bg relative overflow-hidden pt-[120px] pb-12 sm:pt-[140px] md:pb-16"
        >
          <div className="pointer-events-none absolute left-[10%] top-[20%] h-[400px] w-[400px] rounded-full bg-[radial-gradient(circle,rgba(234,88,12,0.18)_0%,transparent_70%)]" />
          <div className="pointer-events-none absolute right-[8%] top-[35%] h-[350px] w-[350px] rounded-full bg-[radial-gradient(circle,rgba(245,158,11,0.12)_0%,transparent_70%)]" />

          <div className="mx-auto w-full max-w-7xl 2xl:px-6 xl:px-5 lg:px-4 md:px-4 px-4">
            <div className="mx-auto max-w-3xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="mb-6 inline-flex items-center gap-2"
              >
                <div className="flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5">
                  <LayoutGrid size={14} color="var(--accent-amber)" />
                  <span className="text-xs font-medium text-accent-amber sm:text-sm">
                    Investment sectors
                  </span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="font-display text-[clamp(36px,7vw,72px)] leading-[1.05] text-text-primary"
              >
                Explore Our <span className="text-ember">Investment</span> Sectors
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mx-auto mt-5 max-w-2xl text-[clamp(15px,2vw,18px)] leading-relaxed text-text-muted"
              >
                MineMe allocates capital across four audited sectors — crypto mining,
                bullion trade, pharmaceutical R&amp;D, and AI infrastructure. Browse
                operators and listed names by sector below.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="mt-8 flex flex-wrap justify-center gap-3"
              >
                {VALUE_PILLS.map(({ icon: Icon, label }) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-secondary px-3 py-1.5 text-xs text-text-muted sm:text-sm"
                  >
                    <Icon size={14} className="text-accent-amber" aria-hidden />
                    {label}
                  </span>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        <section className="bg-bg-primary 2xl:px-6 xl:px-5 lg:px-4 md:px-4 px-4 py-12 md:py-16">
          <div className="mx-auto max-w-7xl">
            <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <TabsList className="flex flex-wrap gap-2 rounded-xl border border-border bg-bg-secondary p-1">
                  {PARTNER_CATEGORIES.map((cat) => {
                    const Icon = CATEGORY_ICONS[cat.iconName] || Sparkles;
                    return (
                      <TabsTrigger key={cat.id} value={cat.id} className={triggerBase}>
                        <Icon size={14} aria-hidden />
                        <span className="hidden xs:inline sm:inline">{cat.label}</span>
                        <span className="rounded-full bg-bg-card px-1.5 py-0.5 text-[10px] font-mono text-text-muted">
                          {cat.partners.length}
                        </span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>

              {PARTNER_CATEGORIES.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.iconName] || Sparkles;
                return (
                  <TabsContent key={cat.id} value={cat.id} className="mt-4">
                    <div className="mb-4 flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-accent-amber">
                        <Icon size={18} aria-hidden />
                      </div>
                      <div>
                        <h2 className="font-display text-lg font-semibold text-text-primary sm:text-xl">
                          {cat.label}
                        </h2>
                        <p className="text-xs text-text-muted sm:text-sm">{cat.tagline}</p>
                      </div>
                    </div>
                    <PartnersTable
                      partners={cat.partners}
                      preset={cat.partnersTablePreset === 'ticker' ? 'ticker' : 'directory'}
                      quotes={quotes}
                      syncUrl
                      urlTab={tab}
                      tabId={cat.id}
                      listPath={INVESTMENT_SECTORS_PATH}
                      emptyHint={`No ${cat.label.toLowerCase()} listings match the current filters.`}
                    />
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
